import { useState, useMemo, useCallback, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { products } from '@/mocks/products';
import { categories } from '@/mocks/categories';

const fallbackImage = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22400%22 viewBox=%220 0 400 400%22%3E%3Crect fill=%22%23f5f5f0%22 width=%22400%22 height=%22400%22/%3E%3Ctext fill=%22%23999%22 font-family=%22sans-serif%22 font-size=%2216%22 dy=%22.3em%22 text-anchor=%22middle%22 x=%22200%22 y=%22200%22%3EProduct Image%3C/text%3E%3C/svg%3E';

const categoriesWithProducts = categories.filter(cat =>
  products.some(p => p.category === cat.id)
);

export default function ProductsSection() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(categoriesWithProducts[0]?.id || 'shoes');

  const filteredProducts = useMemo(() =>
    products.filter(p => p.category === activeCategory),
    [activeCategory]
  );

  const activeCatData = useMemo(() =>
    categoriesWithProducts.find(c => c.id === activeCategory),
    [activeCategory]
  );

  const handleImgError = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.src = fallbackImage;
  }, []);

  return (
    <section id="products" className="py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
            {t('products.title')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
            {t('products.subtitle')}
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8 md:mb-10 scrollbar-hide">
          {categoriesWithProducts.map((cat) => {
            const count = products.filter(p => p.category === cat.id).length;
            const isActive = cat.id === activeCategory;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap
                  transition-all duration-200 cursor-pointer shrink-0
                  ${isActive
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300 hover:bg-primary-50'
                  }
                `}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={cat.icon}></i>
                </span>
                <span>{t(`categories.${cat.nameKey}`)}</span>
                <span className={`
                  text-xs px-1.5 py-0.5 rounded-full
                  ${isActive ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-600'}
                `}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {filteredProducts.map((product) => (
            <a
              key={product.id}
              href={product.productUrl}
              target="_blank"
              rel="noopener nofollow"
              data-product-shop
              className="group flex flex-col overflow-hidden rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-200 hover:shadow-md"
            >
              {/* Product image */}
              <div className="relative aspect-square overflow-hidden bg-background-200">
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  title={product.name}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={handleImgError}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Product info */}
              <div className="flex flex-col flex-1 p-3 md:p-4">
                <span className="inline-flex items-center gap-1 text-xs text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-full w-fit mb-2">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={activeCatData?.icon || 'ri-price-tag-3-line'}></i>
                  </span>
                  {t(`categories.${activeCatData?.nameKey || 'shoes'}`)}
                </span>

                <h3 className="text-sm font-medium text-foreground-900 leading-snug mb-2 line-clamp-2 flex-1">
                  {product.name}
                </h3>

                <p className="text-sm font-bold text-primary-600 mb-3">
                  {product.price}
                </p>

                <button className="w-full py-2 px-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                  {t('products.buyNow')}
                </button>
              </div>
            </a>
          ))}
        </div>

        {/* View all CTA */}
        {activeCatData && (
          <div className="mt-8 md:mt-10 text-center">
            <a
              href={activeCatData.url}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-900 transition-colors"
            >
              {t('products.viewAll')}
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}